import {
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  memo,
  useRef,
} from "react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { MaterialContext } from "../context/MaterialContext";

import navBarStyle from "../styles/nav.module.scss";
import FilterBox from "./FilterBox";
import UserBox from "./UserBox";
import {
  downArrowIcon,
  designerIcon,
  vendorIcon,
  guestIcon,
  closeIcon,
} from "./Icons";
import SearchBar from "./SearchBar";

function NavBar() {
  const { collection, userInformation, changelogOutUser, media } =
    useContext(UserContext);
  const { isFilter } = useContext(MaterialContext);
  const [userMenuIsVisible, setUserMenuIsVisible] = useState(false);
  const [filterMenuIsVisible, setFilterMenuIsVisible] = useState(false);
  const [mobileNavHidden, setMobileNavHidden] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const lastScrollYRef = useRef(0);
  const scrollTickingRef = useRef(false);
  const scrollRafRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isLibraryRoute = location.pathname === "/library";
  const isSingleMaterialRoute =
    /^\/[^/]+\/[^/]+$/.test(location.pathname) &&
    !location.pathname.startsWith("/material/");
  const isImmersiveMobileRoute =
    location.pathname === "/help" || isSingleMaterialRoute;
  const collectionCount = collection?.length || 0;

  const activeUserIcon = useMemo(() => {
    if (userInformation.userType === "vendor") {
      return vendorIcon;
    }
    if (userInformation.userType === "designer") {
      return designerIcon;
    }
    return guestIcon;
  }, [userInformation.userType]);

  useEffect(() => {
    if (location.pathname !== "/library") {
      setUserMenuIsVisible(false);
      setFilterMenuIsVisible(false);
    }
    setMobileActionsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (media !== "mobile") {
      document.body.style.overflow = "";
      setMobileNavHidden(false);
      return;
    }

    if (isImmersiveMobileRoute) {
      setMobileNavHidden(false);
      return;
    }

    lastScrollYRef.current = window.scrollY;

    const updateNavOnScroll = () => {
      const currentScrollY = window.scrollY;

      if (mobileActionsOpen) {
        setMobileNavHidden(false);
        lastScrollYRef.current = currentScrollY;
        scrollTickingRef.current = false;
        return;
      }

      if (currentScrollY <= 30) {
        setMobileNavHidden(false);
        lastScrollYRef.current = currentScrollY;
        scrollTickingRef.current = false;
        return;
      }

      const scrollDelta = currentScrollY - lastScrollYRef.current;

      if (scrollDelta > 8) {
        setMobileNavHidden((prev) => (prev ? prev : true));
      } else if (scrollDelta < -8) {
        setMobileNavHidden((prev) => (prev ? false : prev));
      }

      lastScrollYRef.current = currentScrollY;
      scrollTickingRef.current = false;
    };

    const handleScroll = () => {
      if (scrollTickingRef.current) {
        return;
      }

      scrollTickingRef.current = true;
      scrollRafRef.current = window.requestAnimationFrame(updateNavOnScroll);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollRafRef.current) {
        window.cancelAnimationFrame(scrollRafRef.current);
      }
      scrollTickingRef.current = false;
    };
  }, [media, isImmersiveMobileRoute, mobileActionsOpen]);

  useEffect(() => {
    if (!mobileActionsOpen) {
      return;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMobileActionsOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [mobileActionsOpen]);

  useEffect(() => {
    if (media === "mobile") {
      document.body.style.overflow = mobileActionsOpen ? "hidden" : "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [media, mobileActionsOpen]);

  const handleClickAction = useCallback(
    (navigateTo) => {
      navigate(navigateTo);
    },
    [navigate],
  );

  const handleDropDownMenu = useCallback((type) => {
    if (type === "user") {
      setUserMenuIsVisible((prev) => !prev);
      setFilterMenuIsVisible(false);
      return;
    }

    if (type === "filter") {
      setUserMenuIsVisible(false);
      setFilterMenuIsVisible((prev) => !prev);
    }
  }, []);

  const handleMobileBack = useCallback(() => {
    navigate("/library");
  }, [navigate]);

  const handleMobileNavigate = useCallback(
    (path) => {
      navigate(path);
      setMobileActionsOpen(false);
    },
    [navigate],
  );

  const handleMobileMenuToggle = useCallback(
    (type) => {
      setMobileActionsOpen(false);
      handleDropDownMenu(type);
    },
    [handleDropDownMenu],
  );

  const handleToggleMobileActions = useCallback(() => {
    setMobileActionsOpen((value) => !value);
  }, []);

  const handleCloseMobileActions = useCallback(() => {
    setMobileActionsOpen(false);
  }, []);

  const handleShowFilterMenu = useCallback(() => {
    handleDropDownMenu("filter");
  }, [handleDropDownMenu]);

  const handleShowUserMenu = useCallback(() => {
    handleDropDownMenu("user");
  }, [handleDropDownMenu]);

  const handleShowFilterMenuMobile = useCallback(() => {
    handleMobileMenuToggle("filter");
  }, [handleMobileMenuToggle]);

  const handleShowUserMenuMobile = useCallback(() => {
    handleMobileMenuToggle("user");
  }, [handleMobileMenuToggle]);

  return (
    <>
      <FilterBox
        filterMenuIsVisible={filterMenuIsVisible}
        setFilterMenuIsVisible={setFilterMenuIsVisible}
      />
      <UserBox
        userInformation={userInformation}
        changelogOutUser={changelogOutUser}
        userMenuIsVisible={userMenuIsVisible}
        setUserMenuIsVisible={setUserMenuIsVisible}
      />

      {media === "mobile" ? (
        <MobileNav
          isImmersiveMobileRoute={isImmersiveMobileRoute}
          mobileNavHidden={mobileNavHidden}
          mobileActionsOpen={mobileActionsOpen}
          collectionCount={collectionCount}
          activeUserIcon={activeUserIcon}
          userType={userInformation.userType}
          isLibraryRoute={isLibraryRoute}
          isFilter={isFilter}
          filterMenuIsVisible={filterMenuIsVisible}
          userMenuIsVisible={userMenuIsVisible}
          onBack={handleMobileBack}
          onNavigate={handleMobileNavigate}
          onToggleMobileActions={handleToggleMobileActions}
          onCloseMobileActions={handleCloseMobileActions}
          onShowFilter={handleShowFilterMenuMobile}
          onShowUser={handleShowUserMenuMobile}
          onLogoClick={handleClickAction}
        />
      ) : (
        <DesktopNav
          isLibraryRoute={isLibraryRoute}
          isFilter={isFilter}
          filterMenuIsVisible={filterMenuIsVisible}
          userMenuIsVisible={userMenuIsVisible}
          collectionCount={collectionCount}
          activeUserIcon={activeUserIcon}
          onShowFilter={handleShowFilterMenu}
          onShowUser={handleShowUserMenu}
          onLogoClick={handleClickAction}
        />
      )}
    </>
  );
}

const MobileNav = memo(function MobileNav({
  isImmersiveMobileRoute,
  mobileNavHidden,
  mobileActionsOpen,
  collectionCount,
  activeUserIcon,
  userType,
  isLibraryRoute,
  isFilter,
  filterMenuIsVisible,
  userMenuIsVisible,
  onBack,
  onNavigate,
  onToggleMobileActions,
  onCloseMobileActions,
  onShowFilter,
  onShowUser,
  onLogoClick,
}) {
  const menuToggleButtonRef = useRef(null);
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const panelElement = panelRef.current;

    if (!mobileActionsOpen || !panelElement) {
      if (wasOpenRef.current && menuToggleButtonRef.current) {
        menuToggleButtonRef.current.focus();
      }
      wasOpenRef.current = false;
      return;
    }

    wasOpenRef.current = true;

    const focusableSelector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const getFocusableElements = () => {
      return Array.from(
        panelElement.querySelectorAll(focusableSelector),
      ).filter((element) => {
        return !element.hasAttribute("disabled");
      });
    };

    if (closeButtonRef.current) {
      closeButtonRef.current.focus();
    } else {
      panelElement.focus();
    }

    const trapFocus = (event) => {
      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        event.preventDefault();
        panelElement.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey) {
        if (activeElement === firstElement || activeElement === panelElement) {
          event.preventDefault();
          lastElement.focus();
        }
        return;
      }

      if (activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    panelElement.addEventListener("keydown", trapFocus);

    return () => {
      panelElement.removeEventListener("keydown", trapFocus);
    };
  }, [mobileActionsOpen]);

  if (isImmersiveMobileRoute) {
    return (
      <div className={`${navBarStyle.bar} ${navBarStyle.mobileMinimalBar}`}>
        <button
          type="button"
          className={navBarStyle.mobileBackButton}
          onClick={onBack}
        >
          Back
        </button>
        <div className={navBarStyle.mobileMinimalTitle}>MATLIB</div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`${navBarStyle.bar} ${navBarStyle.mobileBar} ${
          mobileNavHidden ? navBarStyle.mobileBarHidden : ""
        }`}
      >
        <div
          onClick={() => onLogoClick("/library")}
          className={navBarStyle.logo}
        >
          MATLIB
        </div>

        <div className={navBarStyle.mobileNavigation}>
          <NavLink
            className={({ isActive }) =>
              isActive ? navBarStyle.mobileActiveLink : ""
            }
            to="library"
          >
            Library
          </NavLink>
          <NavLink
            className={({ isActive }) =>
              isActive ? navBarStyle.mobileActiveLink : ""
            }
            to="collection"
          >
            Collection
            <span className={navBarStyle.mobileCollectionCount}>
              {collectionCount}
            </span>
          </NavLink>
          <button
            ref={menuToggleButtonRef}
            type="button"
            className={`${navBarStyle.mobileActionButton} ${
              mobileActionsOpen ? navBarStyle.active : ""
            }`}
            onClick={onToggleMobileActions}
            aria-expanded={mobileActionsOpen}
            aria-controls="mobile-menu-panel"
            aria-label="Open navigation menu"
          >
            Menu
            {downArrowIcon}
          </button>
        </div>
      </div>

      <div
        className={`${navBarStyle.mobilePanelOverlay} ${
          mobileActionsOpen ? "" : navBarStyle.mobilePanelOverlayHidden
        }`}
        onClick={onCloseMobileActions}
        aria-hidden={!mobileActionsOpen}
      ></div>

      <div
        id="mobile-menu-panel"
        ref={panelRef}
        className={`${navBarStyle.mobileActionPanel} ${
          mobileActionsOpen ? "" : navBarStyle.mobilePanelHidden
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation menu"
        aria-hidden={!mobileActionsOpen}
        tabIndex={-1}
      >
        <div className={navBarStyle.mobilePanelHeader}>
          <div className={navBarStyle.mobilePanelTitle}>Menu</div>
          <button
            ref={closeButtonRef}
            type="button"
            className={navBarStyle.mobilePanelClose}
            onClick={onCloseMobileActions}
            aria-label="Close navigation menu"
          >
            {closeIcon}
          </button>
        </div>

        <div className={navBarStyle.mobilePanelRole}>
          {activeUserIcon}
          <span>{userType || "guest"}</span>
        </div>

        <SearchBar />

        <div className={navBarStyle.mobileDrawerNav}>
          <button
            type="button"
            className={navBarStyle.mobilePanelButton}
            onClick={() => onNavigate("/library")}
          >
            Library
          </button>
          <button
            type="button"
            className={navBarStyle.mobilePanelButton}
            onClick={() => onNavigate("/collection")}
          >
            Collection ({collectionCount})
          </button>
        </div>

        <div className={navBarStyle.mobileActionRow}>
          {isLibraryRoute ? (
            <button
              type="button"
              className={`${navBarStyle.mobilePanelButton} ${
                filterMenuIsVisible ? navBarStyle.active : ""
              }`}
              onClick={onShowFilter}
            >
              {isFilter ? "Filters: On" : "Filters"}
            </button>
          ) : null}
          <button
            type="button"
            className={`${navBarStyle.mobilePanelButton} ${
              userMenuIsVisible ? navBarStyle.active : ""
            }`}
            onClick={onShowUser}
          >
            Account
          </button>
        </div>
      </div>
    </>
  );
});

const DesktopNav = memo(function DesktopNav({
  isLibraryRoute,
  isFilter,
  filterMenuIsVisible,
  userMenuIsVisible,
  collectionCount,
  activeUserIcon,
  onShowFilter,
  onShowUser,
  onLogoClick,
}) {
  return (
    <div className={navBarStyle.bar}>
      <div onClick={() => onLogoClick("/library")} className={navBarStyle.logo}>
        MATLIB
      </div>
      <SearchBar />
      <div className={navBarStyle.navigation}>
        {isLibraryRoute ? (
          <div
            className={
              !filterMenuIsVisible
                ? `${navBarStyle.filter}`
                : `${navBarStyle.filter} ${navBarStyle.active}`
            }
            onClick={onShowFilter}
          >
            {isFilter ? <div>FILTERED</div> : <div>FILTER</div>}
            {downArrowIcon}
          </div>
        ) : null}
        <NavLink
          className={({ isActive }) => (isActive ? "active" : "inactive")}
          to="library"
        >
          LIBRARY
        </NavLink>
        <NavLink
          className={({ isActive }) => (isActive ? "active" : "inactive")}
          to="collection"
        >
          COLLECTION{" "}
          <span className={navBarStyle.collectionCount}>{collectionCount}</span>
        </NavLink>
        <div
          className={
            !userMenuIsVisible
              ? `${navBarStyle.userMenu}`
              : `${navBarStyle.userMenu} ${navBarStyle.active}`
          }
          onClick={onShowUser}
        >
          {activeUserIcon}
        </div>
      </div>
    </div>
  );
});

export default NavBar;
